/**
 * TURN CC · 22 (Design refresh) — Calendar event visual.
 *
 * Two visual classes:
 *  1) `band` — Corporate event, solid full-width colored band, white text,
 *     rounded only on start/end of a row segment. Overlay above briefs.
 *  2) `chip` — Brief pill: tinted background + colored 2px left border +
 *     type icon + status icon, per type/color.
 *
 * Both variants ship with a Radix tooltip on hover.
 */
import { STATUS_META, TYPE_META, formatSGTTime, formatSGTHour } from "./calendarMeta";
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "@/components/ui/tooltip";
import { MapPin, CheckCircle2, AlertTriangle } from "lucide-react";

// --------- CHIP (brief pill) ---------
function Chip({ event, onClick }) {
  const meta = TYPE_META[event.type] || {};
  const TypeIcon = meta.Icon;
  const isDelivered = event.status === "delivered";
  const isFailed = event.status === "failed";
  const isRunning = event.status === "running";
  const isPastFaded = isDelivered;
  const hour = formatSGTHour(event.starts_at);

  return (
    <button
      type="button"
      data-testid={`calendar-event-${event.id}`}
      data-event-type={event.type}
      data-event-status={event.status}
      onClick={(e) => { e.stopPropagation(); onClick?.(event); }}
      className={
        "group w-full text-left flex items-center gap-1 rounded " +
        "pl-1.5 pr-1.5 py-[3px] transition-all " +
        meta.pillClass + " " +
        "hover:brightness-95 dark:hover:brightness-110 " +
        (isRunning ? "ring-1 ring-blue-400/60 dark:ring-blue-400/50 " : "") +
        (isPastFaded ? "opacity-60 " : "")
      }
    >
      {TypeIcon && (
        <TypeIcon className={"h-3 w-3 shrink-0 " + (meta.iconClass || "")} />
      )}
      {hour && (
        <span className="text-[10px] font-semibold tabular-nums tracking-tight opacity-90 shrink-0">
          {hour}
        </span>
      )}
      <span
        aria-hidden="true"
        className="text-[10px] mx-0.5 opacity-40 shrink-0"
      >
        ·
      </span>
      <span className="text-[11px] leading-4 font-medium truncate flex-1">
        {meta.label}
      </span>
      {isFailed && (
        <AlertTriangle className="h-3 w-3 shrink-0 text-red-500 dark:text-red-400" />
      )}
      {isDelivered && !isFailed && (
        <CheckCircle2 className="h-3 w-3 shrink-0 text-emerald-500/80 dark:text-emerald-400/80" />
      )}
      {isRunning && !isFailed && (
        <span className="h-1.5 w-1.5 rounded-full bg-blue-500 dark:bg-blue-400 shrink-0 animate-pulse" />
      )}
    </button>
  );
}

// --------- BAND (corporate event) ---------
function Band({ event, span, isStart, isEnd, onClick }) {
  return (
    <button
      type="button"
      data-testid={`calendar-band-${event.id}`}
      onClick={(e) => { e.stopPropagation(); onClick?.(event); }}
      className={
        "group w-full h-6 text-left flex items-center gap-1.5 " +
        "px-2 truncate text-white text-[11px] font-semibold " +
        "shadow-sm transition-all hover:brightness-110 cursor-pointer " +
        "bg-gradient-to-r from-red-500 to-rose-600 " +
        (isStart ? "rounded-l-md " : "") +
        (isEnd ? "rounded-r-md " : "")
      }
      style={{
        gridColumn: `span ${span} / span ${span}`,
      }}
    >
      <MapPin className="h-3 w-3 shrink-0 text-white/90" />
      {isStart && <span className="truncate leading-none">{event.title}</span>}
    </button>
  );
}

// --------- Wrapper (tooltip + variant switch) ---------
export default function CalendarEvent({ event, onClick, variant = "chip",
                                        span = 1, isStart = false, isEnd = false }) {
  const meta = TYPE_META[event.type] || {};
  const status = STATUS_META[event.status] || STATUS_META.scheduled;
  const tooltipBody = (
    <div className="text-xs space-y-1 min-w-[200px]">
      <div className="flex items-center gap-1.5">
        <span
          className="h-1.5 w-1.5 rounded-full inline-block"
          style={{ background: event.color || meta.color }}
        />
        <span className="font-semibold">{event.title}</span>
      </div>
      <div className="text-hm-fg-subtle">
        <span className="uppercase tracking-widest text-[10px] mono">
          {meta.label}
        </span>
        {" · "}
        <span>{status.label}</span>
      </div>
      {event.starts_at && (
        <div className="text-hm-fg-muted">
          {event.all_day
            ? "All day"
            : `Scheduled ${formatSGTTime(event.starts_at)}`}
        </div>
      )}
      {event.email_sent_at && (
        <div className="text-emerald-600 dark:text-emerald-400">
          Delivered {formatSGTTime(event.email_sent_at)}
        </div>
      )}
      {event.articles_count > 0 && (
        <div className="text-hm-fg-muted">
          {event.articles_count} {event.type === "weekly_quotes" ? "quotes" : "articles"}
        </div>
      )}
      {event.location && (
        <div className="text-hm-fg-muted">{event.location}</div>
      )}
      {event.failure_reason && (
        <div className="text-red-500 dark:text-red-400 truncate">
          {event.failure_reason}
        </div>
      )}
    </div>
  );

  const inner = variant === "band"
    ? <Band event={event} span={span} isStart={isStart} isEnd={isEnd} onClick={onClick} />
    : <Chip event={event} onClick={onClick} />;

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>{inner}</TooltipTrigger>
        <TooltipContent
          side="top"
          className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/10 text-slate-900 dark:text-slate-100 shadow-lg"
        >
          {tooltipBody}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
